import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AnimatedBackground } from '../../components/AnimatedBackground';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';

const TermsAndConditionsPage: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const isSpanish = i18n.language === 'es';

  return (
    <div className="min-h-screen">
      {/* Header */}
      <motion.nav
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="fixed top-0 w-full z-[9999] bg-white/95 backdrop-blur-md border-b border-blue-100 shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-blue-700 hover:text-blue-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">{isSpanish ? 'Volver' : 'Back'}</span>
            </button>
            <LanguageSelector variant="compact" className="text-blue-700" />
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <AnimatedBackground variant="hero" className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {isSpanish ? 'Términos y Condiciones' : 'Terms and Conditions'}
            </h1>
            <p className="text-lg text-blue-200">
              {isSpanish
                ? 'Última actualización: Octubre 2025'
                : 'Last updated: October 2025'}
            </p>
          </motion.div>
        </div>
      </AnimatedBackground>

      {/* Content */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-blue max-w-none"
          >
            {isSpanish ? (
              <>
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Aceptación de los Términos</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Al acceder y utilizar los servicios de ScoutMind AI, aceptas estar sujeto a estos Términos y Condiciones. Si no estás de acuerdo con alguna parte de estos términos, no debes utilizar nuestros servicios.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Descripción de los Servicios</h2>
                  <p className="text-gray-700 leading-relaxed">
                    ScoutMind AI ofrece servicios de consultoría tecnológica especializados en el sector deportivo, incluyendo:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li>Desarrollo de plataformas deportivas personalizadas</li>
                    <li>Análisis de datos y Big Data aplicado al deporte</li>
                    <li>Gestión de proyectos tecnológicos</li>
                    <li>Integración de sistemas</li>
                    <li>Consultoría en transformación digital deportiva</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Uso del Sitio Web</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Al utilizar nuestro sitio web, te comprometes a:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Proporcionar información precisa y actualizada</li>
                    <li>No utilizar el sitio para actividades ilegales o no autorizadas</li>
                    <li>No intentar acceder a áreas restringidas del sitio</li>
                    <li>No transmitir virus, malware o código malicioso</li>
                    <li>Respetar los derechos de propiedad intelectual</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Contratación de Servicios</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Los servicios de consultoría se contratan mediante propuestas personalizadas que detallan:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li>Alcance del proyecto</li>
                    <li>Plazos de entrega</li>
                    <li>Coste y forma de pago</li>
                    <li>Entregables específicos</li>
                    <li>Términos de soporte y mantenimiento</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    Cada proyecto se formaliza mediante un contrato específico que complementa estos términos generales.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Propiedad Intelectual</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Todo el contenido del sitio web de ScoutMind AI, incluyendo textos, gráficos, logos, iconos, imágenes y software, es propiedad de ScoutMind AI o sus proveedores de contenido y está protegido por las leyes de propiedad intelectual.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    Los derechos de propiedad intelectual sobre los desarrollos realizados para clientes se establecen específicamente en cada contrato de servicios.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Confidencialidad</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Nos comprometemos a mantener la confidencialidad de toda la información sensible compartida durante la prestación de servicios. Ambas partes se obligan a:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li>No divulgar información confidencial a terceros</li>
                    <li>Utilizar la información solo para los fines acordados</li>
                    <li>Proteger la información con medidas de seguridad adecuadas</li>
                    <li>Devolver o destruir la información al finalizar el proyecto</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Pagos y Facturación</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Los términos de pago se especifican en cada propuesta comercial. Generalmente incluyen:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li>Pago por hitos del proyecto</li>
                    <li>Facturación mensual para retainers</li>
                    <li>Términos de pago neto 30 días</li>
                    <li>Penalizaciones por pagos tardíos</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cancelación y Reembolsos</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Las políticas de cancelación varían según el tipo de servicio contratado:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li><strong>Proyectos fijos:</strong> Cancelación con 15 días de antelación. Se facturará el trabajo realizado hasta la fecha.</li>
                    <li><strong>Consultoría por horas:</strong> Puede cancelarse en cualquier momento. Solo se facturan las horas trabajadas.</li>
                    <li><strong>Retainer mensual:</strong> Cancelación con 30 días de antelación.</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitación de Responsabilidad</h2>
                  <p className="text-gray-700 leading-relaxed">
                    ScoutMind AI no será responsable de:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li>Pérdidas indirectas o consecuentes</li>
                    <li>Daños causados por el uso indebido de nuestros servicios</li>
                    <li>Interrupciones del servicio por causas de fuerza mayor</li>
                    <li>Fallos en sistemas de terceros</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    Nuestra responsabilidad máxima se limitará al valor del contrato específico del proyecto en cuestión.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Modificaciones de los Términos</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación en el sitio web. El uso continuado de nuestros servicios constituye la aceptación de los términos modificados.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Ley Aplicable y Jurisdicción</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Estos términos se rigen por las leyes de España. Cualquier disputa se resolverá en los tribunales de España, renunciando las partes expresamente a cualquier otro fuero que pudiera corresponderles.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contacto</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Para cualquier consulta sobre estos términos y condiciones:
                  </p>
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-gray-800"><strong>Email:</strong> <a href="mailto:info@scoutmindai.com" className="text-blue-600 hover:text-blue-800">info@scoutmindai.com</a></p>
                    <p className="text-gray-800"><strong>Teléfono:</strong> +34 608 755 358</p>
                    <p className="text-gray-800"><strong>Ubicación:</strong> España</p>
                  </div>
                </section>
              </>
            ) : (
              <>
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                  <p className="text-gray-700 leading-relaxed">
                    By accessing and using ScoutMind AI services, you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, you should not use our services.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Services</h2>
                  <p className="text-gray-700 leading-relaxed">
                    ScoutMind AI offers specialized technology consulting services in the sports sector, including:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li>Development of custom sports platforms</li>
                    <li>Data analysis and Big Data applied to sports</li>
                    <li>Technology project management</li>
                    <li>System integration</li>
                    <li>Sports digital transformation consulting</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Website Usage</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    By using our website, you agree to:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Provide accurate and updated information</li>
                    <li>Not use the site for illegal or unauthorized activities</li>
                    <li>Not attempt to access restricted areas of the site</li>
                    <li>Not transmit viruses, malware, or malicious code</li>
                    <li>Respect intellectual property rights</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Service Contracting</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Consulting services are contracted through personalized proposals that detail:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li>Project scope</li>
                    <li>Delivery timelines</li>
                    <li>Cost and payment terms</li>
                    <li>Specific deliverables</li>
                    <li>Support and maintenance terms</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    Each project is formalized through a specific contract that complements these general terms.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Intellectual Property</h2>
                  <p className="text-gray-700 leading-relaxed">
                    All content on the ScoutMind AI website, including text, graphics, logos, icons, images, and software, is owned by ScoutMind AI or its content providers and is protected by intellectual property laws.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    Intellectual property rights over developments made for clients are specifically established in each service contract.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Confidentiality</h2>
                  <p className="text-gray-700 leading-relaxed">
                    We commit to maintaining the confidentiality of all sensitive information shared during service delivery. Both parties agree to:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li>Not disclose confidential information to third parties</li>
                    <li>Use information only for agreed purposes</li>
                    <li>Protect information with appropriate security measures</li>
                    <li>Return or destroy information upon project completion</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Payments and Billing</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Payment terms are specified in each commercial proposal. Generally they include:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li>Payment by project milestones</li>
                    <li>Monthly billing for retainers</li>
                    <li>Net 30 payment terms</li>
                    <li>Penalties for late payments</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cancellation and Refunds</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Cancellation policies vary depending on the type of service contracted:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li><strong>Fixed projects:</strong> Cancellation with 15 days notice. Work completed to date will be billed.</li>
                    <li><strong>Hourly consulting:</strong> Can be canceled at any time. Only hours worked are billed.</li>
                    <li><strong>Monthly retainer:</strong> Cancellation with 30 days notice.</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitation of Liability</h2>
                  <p className="text-gray-700 leading-relaxed">
                    ScoutMind AI will not be responsible for:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li>Indirect or consequential losses</li>
                    <li>Damages caused by misuse of our services</li>
                    <li>Service interruptions due to force majeure</li>
                    <li>Third-party system failures</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    Our maximum liability will be limited to the value of the specific project contract in question.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Terms Modifications</h2>
                  <p className="text-gray-700 leading-relaxed">
                    We reserve the right to modify these terms at any time. Changes will take effect immediately after publication on the website. Continued use of our services constitutes acceptance of the modified terms.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Applicable Law and Jurisdiction</h2>
                  <p className="text-gray-700 leading-relaxed">
                    These terms are governed by the laws of Spain. Any dispute will be resolved in the courts of Spain, with parties expressly waiving any other jurisdiction that may apply.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact</h2>
                  <p className="text-gray-700 leading-relaxed">
                    For any questions about these terms and conditions:
                  </p>
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-gray-800"><strong>Email:</strong> <a href="mailto:info@scoutmindai.com" className="text-blue-600 hover:text-blue-800">info@scoutmindai.com</a></p>
                    <p className="text-gray-800"><strong>Phone:</strong> +34 608 755 358</p>
                    <p className="text-gray-800"><strong>Location:</strong> Spain</p>
                  </div>
                </section>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
